import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
// import { useAuth } from '../contexts/AuthContext'; // Unused for now
import { toast } from 'react-toastify';
import styles from '../styles/Notifications.module.scss';

interface Notification {
  id: string;
  type: 'info' | 'warning' | 'emergency' | 'success';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  actionUrl?: string;
  actionText?: string;
}

const Notifications: React.FC = () => {
  // const { currentUser } = useAuth(); // Unused for now
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [filter, setFilter] = useState<'all' | 'unread' | 'emergency'>('all');
  const [isLoading, setIsLoading] = useState(true);

  // Mock notifications data
  useEffect(() => {
    const mockNotifications: Notification[] = [
      {
        id: '1',
        type: 'emergency',
        title: 'High Tide Alert',
        message: 'Extremely high tide expected at Marina Beach, Chennai at 3:30 PM today. Exercise caution near coastal areas.',
        timestamp: new Date(Date.now() - 30 * 60 * 1000), // 30 minutes ago
        read: false,
        actionUrl: '/dashboard',
        actionText: 'View Details'
      },
      {
        id: '2',
        type: 'warning',
        title: 'Weather Advisory',
        message: 'Moderate to rough sea conditions expected along the west coast. Small craft advisory in effect.',
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
        read: false,
        actionUrl: '/dashboard',
        actionText: 'Check Map'
      },
      {
        id: '3',
        type: 'info',
        title: 'System Update',
        message: 'OceanGuard has been updated with improved marker accuracy and new safety features.',
        timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000), // 1 day ago
        read: true,
        actionUrl: '/about',
        actionText: 'Learn More'
      },
      {
        id: '4',
        type: 'success',
        title: 'Report Acknowledged',
        message: 'Your ocean hazard report has been verified and added to the safety database. Thank you for contributing!',
        timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000), // 3 days ago
        read: true,
        actionUrl: '/dashboard',
        actionText: 'View Report'
      },
      {
        id: '5',
        type: 'info',
        title: 'Welcome to OceanGuard',
        message: 'Thank you for joining OceanGuard! Explore the dashboard to start monitoring ocean safety in your area.',
        timestamp: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // 1 week ago
        read: true,
        actionUrl: '/dashboard',
        actionText: 'Get Started'
      }
    ];

    // Simulate loading
    setTimeout(() => {
      setNotifications(mockNotifications);
      setIsLoading(false);
    }, 1000);
  }, []);

  const filteredNotifications = notifications.filter(notification => {
    if (filter === 'unread') return !notification.read;
    if (filter === 'emergency') return notification.type === 'emergency';
    return true;
  });

  const unreadCount = notifications.filter(n => !n.read).length;

  const markAsRead = (id: string) => {
    setNotifications(prev => 
      prev.map(notification => 
        notification.id === id 
          ? { ...notification, read: true }
          : notification
      )
    );
  };

  const markAllAsRead = () => {
    setNotifications(prev => 
      prev.map(notification => ({ ...notification, read: true }))
    );
    toast.success('All notifications marked as read', {
      position: 'top-right',
      autoClose: 3000,
    });
  };

  const deleteNotification = (id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
    toast.success('Notification deleted', {
      position: 'top-right',
      autoClose: 3000,
    });
  };

  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'emergency':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
          </svg>
        );
      case 'warning':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
          </svg>
        );
      case 'success':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
          </svg>
        );
      default:
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
          </svg>
        );
    }
  };

  const formatTimestamp = (timestamp: Date) => {
    const now = new Date();
    const diff = now.getTime() - timestamp.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 60) {
      return `${minutes}m ago`;
    } else if (hours < 24) {
      return `${hours}h ago`;
    } else {
      return `${days}d ago`;
    }
  };

  if (isLoading) {
    return (
      <div className={styles.notificationsPage}>
        <div className={styles.container}>
          <div className={styles.loadingState}>
            <div className={styles.spinner}></div>
            <p>Loading notifications...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className={styles.notificationsPage}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div className={styles.container}>
        {/* Header */}
        <motion.div
          className={styles.header}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className={styles.titleSection}>
            <h1 className={styles.title}>
              <span className={styles.titleHighlight}>Notifications</span>
            </h1>
            {unreadCount > 0 && (
              <span className={styles.unreadBadge}>{unreadCount} unread</span>
            )}
          </div>
          <p className={styles.subtitle}>
            Stay updated with ocean safety alerts and system notifications.
          </p>
        </motion.div>

        {/* Controls */}
        <motion.div
          className={styles.controls}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <div className={styles.filters}>
            {(['all', 'unread', 'emergency'] as const).map((filterType) => (
              <button
                key={filterType}
                className={`${styles.filterButton} ${filter === filterType ? styles.active : ''}`}
                onClick={() => setFilter(filterType)}
              >
                {filterType.charAt(0).toUpperCase() + filterType.slice(1)}
                {filterType === 'unread' && unreadCount > 0 && (
                  <span className={styles.filterBadge}>{unreadCount}</span>
                )}
              </button>
            ))}
          </div>

          {unreadCount > 0 && (
            <button className={styles.markAllButton} onClick={markAllAsRead}>
              Mark all as read
            </button>
          )}
        </motion.div>

        {/* Notifications List */}
        <motion.div
          className={styles.notificationsList}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <AnimatePresence>
            {filteredNotifications.length > 0 ? (
              filteredNotifications.map((notification, index) => (
                <motion.div
                  key={notification.id}
                  className={`${styles.notificationCard} ${styles[notification.type]} ${!notification.read ? styles.unread : ''}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  onClick={() => !notification.read && markAsRead(notification.id)}
                >
                  <div className={styles.notificationIcon}>
                    {getNotificationIcon(notification.type)}
                  </div>

                  <div className={styles.notificationContent}>
                    <div className={styles.notificationHeader}>
                      <h3 className={styles.notificationTitle}>{notification.title}</h3>
                      <div className={styles.notificationMeta}>
                        <span className={styles.timestamp}>
                          {formatTimestamp(notification.timestamp)}
                        </span>
                        {!notification.read && (
                          <span className={styles.unreadDot}></span>
                        )}
                      </div>
                    </div>
                    
                    <p className={styles.notificationMessage}>{notification.message}</p>
                    
                    {notification.actionUrl && (
                      <div className={styles.notificationActions}>
                        <a href={notification.actionUrl} className={styles.actionButton}>
                          {notification.actionText || 'View'}
                        </a>
                      </div>
                    )}
                  </div>

                  <button
                    className={styles.deleteButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteNotification(notification.id);
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                    </svg>
                  </button>
                </motion.div>
              ))
            ) : (
              <motion.div
                className={styles.emptyState}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className={styles.emptyIcon}>
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/>
                  </svg>
                </div>
                <h3>No notifications</h3>
                <p>
                  {filter === 'unread' 
                    ? "You're all caught up! No unread notifications."
                    : filter === 'emergency'
                    ? "No emergency alerts at this time."
                    : "You don't have any notifications yet."
                  }
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Notifications;
